import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  styled,
  CircularProgress,
} from '@mui/material';
import { TabPanel } from './TabPanel';
import ArrowIcon from '../assets/icons/arrow-icon';
import Checkbox from '@mui/material/Checkbox';
import CheckboxIcon from '../assets/icons/checkbox-icon';
import UncheckedIcon from '../assets/icons/uncheckbox-icon';
import { CuotaPlan, Plan } from '../features/planes/planesAPI';
import { useEffect, useState } from 'react';

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

const TableTitle = styled(Typography)(({ theme }) => ({
  fontWeight: '500',
  fontSize: '.85rem',
  color: theme.palette.grey[400],
}));

const TableBold = styled(Typography)(({ theme }) => ({
  fontWeight: '700',
  fontSize: '.85rem',
  color: theme.palette.grey[500],
}));

const TableRegular = styled(Typography)(({ theme }) => ({
  fontWeight: '500',
  fontSize: '.85rem',
  color: theme.palette.grey[500],
}));

interface Props {
  value: number;
  planes: Plan[];
  status: string;
  selectedCuotas: CuotaPlan[];
  setSelectedCuotas: (cuotas: CuotaPlan[]) => void;
}

export default function PlanesPago(props: Props) {
  const { value, planes, status, selectedCuotas, setSelectedCuotas } = props;

  const [totalAmount, setTotalAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [total, setTotal] = useState(0);
  // Estado para controlar que se expanda de a un accordion
  const [expanded, setExpanded] = useState(-1);

  const fechaActual = new Date();
  fechaActual.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (selectedCuotas.length !== 0) {
      // Calculo de totales, si hay cuotas de planes seleccionados calcula el total de todas las cuotas de planes
      // seleccionadas
      const firstTotal = selectedCuotas.reduce((acc, val) => acc + Number(val.monto), 0);
      setTotalAmount(firstTotal);

      const secondTotal = selectedCuotas.reduce((acc, val) => acc + Number(val.recargo), 0);
      setTotalInterest(secondTotal);

      setTotal(firstTotal + secondTotal);
      // Calculo de totales, si no hay cuotas de planes seleccionados calcula el total de todas las cuotas de planes
    } else if (expanded !== -1) {
      console.log('Expanded')
      const firstTotal = planes[expanded].cuotas.reduce((acc, val) => acc + Number(val.monto), 0);
      setTotalAmount(firstTotal);

      const secondTotal = planes[expanded].cuotas.reduce((acc, val) => acc + Number(val.recargo), 0);
      setTotalInterest(secondTotal);

      setTotal(firstTotal + secondTotal);
    }
  }, [planes, selectedCuotas, expanded]);

  const onSelectCuota = (cuota: CuotaPlan, index: number, indexPP: number) => {
    // Determinamos si la cuota seleccionada es la primera cuota
    const firstQuotaSelected = selectedCuotas.length > 0 &&
      selectedCuotas[0].nro_comprob == cuota.nro_comprob;

    // Si se hizo click en la primera y cuota y ya está seleccionada, limpiamos la selección
    if (firstQuotaSelected) {
      setSelectedCuotas([]);
      return;
    }

    // Determinamos si la cuota seleccionada es la última cuota
    const lastQuota = selectedCuotas.length > 0 &&
      selectedCuotas[selectedCuotas.length - 1].nro_comprob == cuota.nro_comprob;

    // Seleccionamos todas las cuotas anteriores a la que se hizo click y, dependiendo de si ya está seleccionada,
    // la cuota en que se hizo click la excluimos o incluimos
    const newSelection = planes[indexPP].cuotas.filter((_, i) => lastQuota ? i < index : i <= index);

    setSelectedCuotas(newSelection);

  };

  function parseDate(dateString: string) {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // Meses son 0-indexados
  }

  const expandFunction = (i: number) => {
    if (i === expanded) {
      setExpanded(-1);
    } else {
      setSelectedCuotas([]);
      setExpanded(i);
    }
  };


  return (
    <TabPanel value={value} index={1}>
      <>
        {status === 'loading' || status === 'idle' ? (
          <CircularProgress size={20} />
        ) : (
          <>
            {planes.length >= 1 ? (
              <>
                {planes.map((plan, index) => (

                  <Accordion
                    expanded={expanded === index}
                    key={index}
                    onChange={() => expandFunction(index)}
                  >
                    <AccordionSummary
                      sx={{
                        flexDirection: 'row-reverse',
                        backgroundColor: '#F5F8FA',
                        marginBottom: '10px',
                        paddingLeft: '15px',
                      }}
                      expandIcon={<ArrowIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography sx={{ paddingLeft: '1rem' }}>
                        Plan {plan.nro_plan} - Cuotas: {plan.cant_cuotas} [
                        {plan.estado}]
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper}>
                        <Table
                          sx={{ minWidth: 650 }}
                          size="small"
                          aria-label="simple table"
                        >
                          <TableHead sx={{ height: '2.5rem' }}>
                            <TableRow>
                              <TableCell></TableCell>
                              <TableCell align="center">
                                <TableTitle>Cuota</TableTitle>
                              </TableCell>
                              <TableCell align="center">
                                <TableTitle>Vencimiento</TableTitle>
                              </TableCell>
                              <TableCell align="center">
                                <TableTitle>Monto</TableTitle>
                              </TableCell>
                              <TableCell align="center">
                                <TableTitle>Interes</TableTitle>
                              </TableCell>
                              <TableCell align="center">
                                <TableTitle>Total</TableTitle>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>


                            {(() => {
                              const hayCuotasVencidas = plan.cuotas?.some(cuota => parseDate(cuota.fecha_vto) < fechaActual);

                              return plan.cuotas?.map((cuota, i) => {

                                const cuotaVencida = parseDate(cuota.fecha_vto) > fechaActual && hayCuotasVencidas;

                                return (
                                  <TableRow
                                    key={i}
                                    sx={{
                                      '&:last-child td, &:last-child th': {
                                        border: 0,
                                      },
                                    }}
                                  >
                                    <TableCell align="center">
                                      {
                                        plan.estado === 'VIGENTE' && !cuota.fecha_pago
                                          ? <Checkbox
                                            disabled={cuotaVencida ? true : false}
                                            checked={
                                              selectedCuotas?.some(item => item.nro_comprob === cuota.nro_comprob)
                                            }
                                            onClick={() => {
                                              // Cuota, Index de cuota, Index de PP
                                              onSelectCuota(cuota, i, index);
                                            }}
                                            icon={<UncheckedIcon />}
                                            checkedIcon={<CheckboxIcon />}
                                            {...label}
                                          />
                                          : <div style={{ height: '36px' }} />
                                      }
                                    </TableCell>
                                    <TableCell align="center">
                                      <TableRegular>
                                        {cuota.cuota}
                                      </TableRegular>
                                    </TableCell>
                                    <TableCell align="center">
                                      <TableRegular>
                                        {cuota.fecha_vto}
                                      </TableRegular>
                                    </TableCell>
                                    <TableCell align="center">
                                      <TableBold>
                                        $ {Number(cuota.monto).toFixed(2)}
                                      </TableBold>
                                    </TableCell>
                                    <TableCell align="center">
                                      <TableBold>
                                        $ {Number(cuota.recargo).toFixed(2)}
                                      </TableBold>
                                    </TableCell>
                                    <TableCell align="center">
                                      <TableBold>
                                        $ {(Number(cuota.recargo) + Number(cuota.monto)).toFixed(2)}
                                      </TableBold>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            })()}
                            <TableRow sx={{ height: '3rem' }}>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                              <TableCell align="center">
                                <TableTitle>TOTAL</TableTitle>
                              </TableCell>
                              <TableCell align="center">
                                <TableBold>
                                  $ {totalAmount.toFixed(2)}
                                </TableBold>
                              </TableCell>
                              <TableCell align="center">
                                <TableBold>
                                  $ {totalInterest.toFixed(2)}
                                </TableBold>
                              </TableCell>
                              <TableCell align="center">
                                <TableBold>$ {total.toFixed(2)}</TableBold>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            ) : (
              <Typography>
                No hay planes de pago generados por el momento
              </Typography>
            )}
          </>
        )}
      </>
    </TabPanel>
  );
}
